var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { Canvas } from "@react-three/fiber";
import { VRButton, XR } from "@react-three/xr";
import { isDesktop, isMobile } from "react-device-detect";
import { forwardRef, useRef } from "react";
import { Perf } from "r3f-perf";
import { Grid } from "@react-three/drei";
// Material ui Imports
import { styled } from "@mui/system";
// Custom components imports (and hooks and helpers)
import VTControls from "./VTControls";
import VTCamera from "./VTCanvasComponents/VTCamera";
import VTCameraControls from "./VTCanvasComponents/VTCameraControls";
// Media imports
//---------------------------------------------------------------------------------------------------
// ## MUI STYLED ##
// Styled canvas
var StyledCanvas = styled(Canvas)(function (_a) {
    var theme = _a.theme;
    return ({
        height: "100%",
        width: "100%",
        "&:hover": {
            cursor: "grab",
        },
        "&:active": {
            cursor: "grabbing",
        },
        backgroundColor: "#191A1D",
    });
});
//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// VT canvas to host other vt components on desktop and vr
export default forwardRef(function (_a, ref) {
    var VR = _a.VR, style = _a.style, debug = _a.debug, debugGrid = _a.debugGrid, position = _a.position, attachToCameraRef = _a.attachToCameraRef, screenShot = _a.screenShot, distanceFromCamera = _a.distanceFromCamera, limitZoom = _a.limitZoom, userMouseDown = _a.userMouseDown, userMouseUp = _a.userMouseUp, onDragged = _a.onDragged, onDragging = _a.onDragging, children = _a.children;
    var mouseMovementDelta = 1.5;
    var startXMouseMovement = useRef(0);
    var startYMouseMovement = useRef(0);
    var mouseDownRef = useRef(false);
    // re-use/avoid duplication of logic for is dragging check logic for dragged and dragging custom events
    function getIsDragging(e) {
        var diffX = Math.abs(e.pageX - startXMouseMovement.current);
        var diffY = Math.abs(e.pageY - startYMouseMovement.current);
        if (diffX > mouseMovementDelta && diffY > mouseMovementDelta) {
            return true;
        }
        else {
            return false;
        }
    }
    //-------------------------------------------------------------------------------------------------
    // ## Render ##
    // VR mode
    if ((!isDesktop && !isMobile) || VR) {
        return (_jsxs(_Fragment, { children: [_jsx(VRButton, {}), _jsx(StyledCanvas, __assign({ style: style, gl: { preserveDrawingBuffer: screenShot ? true : false } }, { children: _jsxs(XR, { children: [_jsx(VTControls, {}), _jsx("pointLight", { position: [10, 10, 10] }), children, " "] }) }))] }));
    }
    //-------------------------------------------------------------------------------------------------
    // ## Render ##
    // Desktop mode
    else {
        return (_jsxs(StyledCanvas, __assign({ onMouseDown: function (e) {
                mouseDownRef.current = true;
                if (onDragged || onDragging) {
                    startXMouseMovement.current = e.pageX;
                    startYMouseMovement.current = e.pageY;
                }
            }, onMouseUp: function (e) {
                mouseDownRef.current = false;
                if (onDragged) {
                    if (getIsDragging(e)) {
                        // dragged
                        onDragged();
                    }
                }
            }, onMouseMove: function (e) {
                if (onDragging && mouseDownRef.current === true && onDragging.allowEventToBeCalled === true) {
                    if (getIsDragging(e)) {
                        // dragging
                        onDragging.callback();
                    }
                }
            }, style: style, gl: { preserveDrawingBuffer: screenShot ? true : false } }, { children: [_jsx("ambientLight", {}), _jsx(VTCameraControls, { ref: ref, screenShot: screenShot, limitZoom: limitZoom, userMouseDown: function () {
                        if (userMouseDown) {
                            userMouseDown();
                        }
                    }, userMouseUp: function () {
                        if (userMouseUp) {
                            userMouseUp();
                        }
                    } }), _jsx(VTCamera, { makeDefault: true, position: position ? position : [0, 0, -0.1], fov: 90, attachToCameraRef: attachToCameraRef, distanceFromCamera: distanceFromCamera }), _jsx("pointLight", { position: [10, 10, 10] }), children, (debug || debugGrid) && _jsx(Perf, {}), debugGrid && _jsx(Grid, { position: [0, 0, 0], cellColor: "red", args: [60, 60] })] })));
    }
});
